/* Boho V2: shared motion, header and pause-motion behaviour.
 * - "Pause motion": one site-wide switch, stored in localStorage (inside try/catch),
 *   mirrored on <html class="bv2-still"> and announced as a `bv2:motion` event.
 * - Settle reveals: only for elements that start below the fold, only when motion
 *   is allowed, and content is visible by default if this file never runs.
 * - Ambient loops run only while their band is on screen.
 * - Header: compact after the first scroll, and dark or white depending on the band under it.
 * - Menus: desktop dropdowns, the phone/tablet drawer (owned here, see below) and the phone
 *   bottom bar lifted above Shopify's preview bar. Loaded async from the header section, so
 *   anything that needs the DOM waits for DOMContentLoaded.
 * Booking links are never touched here: the installed Boulevard widget owns them.
 */
(() => {
  if (window.bohoV2Ready) return;
  window.bohoV2Ready = true;

  const root = document.documentElement;
  const KEY = 'bv2-motion';
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  const store = {
    get() { try { return window.localStorage.getItem(KEY); } catch (e) { return null; } },
    set(v) { try { if (v) window.localStorage.setItem(KEY, v); else window.localStorage.removeItem(KEY); } catch (e) { /* storage blocked: the switch still works for this page */ } },
  };

  let paused = root.classList.contains('bv2-still') || store.get() === 'paused';
  const still = () => paused || reduce.matches;

  function renderToggles() {
    root.classList.toggle('bv2-still', paused);
    document.querySelectorAll('[data-bv2-motion-toggle]').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(paused));
      const label = btn.querySelector('[data-bv2-motion-label]');
      const text = paused ? btn.dataset.labelPlay : btn.dataset.labelPause;
      if (label && text) label.textContent = text;
      if (btn.hasAttribute('data-bv2-motion-icon-only') && text) btn.setAttribute('aria-label', text);
    });
  }

  function setPaused(next) {
    paused = next;
    store.set(paused ? 'paused' : null);
    renderToggles();
    if (still()) revealAll();
    document.dispatchEvent(new CustomEvent('bv2:motion', { detail: { paused: still() } }));
  }

  document.addEventListener('click', (event) => {
    const btn = event.target.closest && event.target.closest('[data-bv2-motion-toggle]');
    if (btn) setPaused(!paused);
  });
  reduce.addEventListener('change', () => document.dispatchEvent(new CustomEvent('bv2:motion', { detail: { paused: still() } })));
  window.bohoV2MotionStill = still;
  window.bohoV2SetPaused = setPaused;
  renderToggles();

  /* ---------- Settle reveals ---------- */
  const REVEAL = [
    'main .bv2 h2',
    'main .bv2 .bv2-lede',
    '.bv2-exp',
    '.bv2-chooser',
    '.bv2-finder__panel',
    '.bv2-finder__result-wrap',
    '.bv2-feel',
    '.bv2-compare',
    '.bv2-reels',
    '.bv2-qa',
    '.bv2-footer__grid',
    '.bv2-lpc-cell',
    '.bv2-lpe__menu',
    '.bv2-lpm__plans',
  ].join(',');
  let observer;
  function revealAll() {
    document.querySelectorAll('[data-bv2-r]').forEach((el) => el.setAttribute('data-bv2-in', ''));
    if (observer) observer.disconnect();
  }
  function setupReveals() {
    if (still() || !('IntersectionObserver' in window)) return;
    const fold = window.innerHeight * 0.92;
    // Measure the band (the Shopify section wrapper) first: a band wholly below the fold hides all
    // its targets and one wholly above keeps them, without asking the browser to lay out a band
    // that content-visibility is still skipping. Only a band across the fold measures its targets.
    const bands = new Map();
    const bandOf = (el) => {
      const band = el.closest('.shopify-section') || el;
      if (!bands.has(band)) bands.set(band, band.getBoundingClientRect());
      return bands.get(band);
    };
    const targets = Array.from(document.querySelectorAll(REVEAL)).filter((el) => {
      const b = bandOf(el);
      if (b.top > fold) return true;
      if (b.bottom < 0) return false;
      return el.getBoundingClientRect().top > fold;
    });
    if (!targets.length) return;
    observer = new IntersectionObserver((entries) => {
      const arriving = entries.filter((e) => e.isIntersecting).map((e) => e.target);
      arriving.forEach((el, i) => {
        el.style.setProperty('--bv2-r-delay', Math.min(i * 90, 270) + 'ms');
        el.setAttribute('data-bv2-in', '');
        observer.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    targets.forEach((el) => { el.setAttribute('data-bv2-r', ''); observer.observe(el); });
    root.classList.add('bv2-reveal-on');
  }

  /* ---------- Loops only while on screen ---------- */
  function setupLoops() {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => e.target.toggleAttribute('data-bv2-off', !e.isIntersecting));
    });
    document.querySelectorAll('.bv2-grad-a, .bv2-grad-b, .bv2-hero, .bv2-footer').forEach((el) => io.observe(el));
  }

  /* Tap to play (2026-10-05): when motion may not autoplay, each clip gets a play/pause button (CSS
   * shows it). Here because service pages do not load boho-v2-loops.js. Nothing loads until pressed;
   * a play pauses off screen and does not resume; a motion change resets all; phones play two. */
  function setupTap() {
    const ico = (d) => '<svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="' + d + '"/></svg>';
    const PLAY = ico('M5 3v10l8-5z');
    const PAUSE = ico('M4 3h3v10H4zm5 0h3v10H9z');
    let order = [];
    const io = window.IntersectionObserver && new IntersectionObserver((es) => es.forEach((e) => { if (!e.isIntersecting && still()) e.target.pause(); }));
    const render = (v) => {
      const b = v.bv2Tap;
      b.setAttribute('aria-label', (v.paused ? 'Play' : 'Pause') + ' clip' + (b.dataset.name ? ': ' + b.dataset.name : ''));
      b.innerHTML = v.paused ? PLAY : PAUSE;
    };
    function press(v) {
      if (!v.paused) return v.pause();
      v.setAttribute('data-bv2-user', '');
      const s = v.querySelector('source[data-src]') || (v.dataset.bv2Src && !v.hasAttribute('data-bv2-loaded') && v.appendChild(document.createElement('source')));
      if (s) { s.src = s.dataset.src || v.dataset.bv2Src; s.removeAttribute('data-src'); v.setAttribute('data-bv2-loaded', ''); v.load(); }
      v.muted = true;
      v.play().catch(() => {});
    }
    function scan() {
      document.querySelectorAll('.bv2 video:not([data-bv2-tap])').forEach((v) => {
        const img = v.parentElement.querySelector('img');
        const b = document.createElement('button');
        v.setAttribute('data-bv2-tap', '');
        if (v.closest('a, button')) return;
        b.type = 'button';
        b.className = 'bv2-tap';
        b.dataset.name = (v.getAttribute('aria-label') || (img && img.alt) || '').replace(/^silent clip:\s*/i, '');
        b.onclick = () => press(v);
        v.bv2Tap = b;
        v.addEventListener('play', () => render(v));
        v.addEventListener('pause', () => render(v));
        v.after(b);
        if (io) io.observe(v);
        render(v);
      });
    }
    document.addEventListener('play', (e) => {
      const v = e.target;
      if (!v.bv2Tap || !still()) return;
      v.setAttribute('data-bv2-user', '');
      order = order.filter((x) => x !== v && !x.paused).concat(v);
      if (window.innerWidth < 810) while (order.length > 2) order.shift().pause();
    }, true);
    document.addEventListener('bv2:motion', () => {
      order = [];
      document.querySelectorAll('video[data-bv2-user]').forEach((v) => { v.removeAttribute('data-bv2-user'); if (still()) v.pause(); });
      if (still()) scan();
    });
    // The buttons only show while motion may not autoplay (CSS), so they are made only then: at start
    // when reduced motion or the pause switch is on, or at the `bv2:motion` change that turns it on.
    const scanIfStill = () => { if (still()) scan(); };
    window.bohoV2TapScan = scanIfStill;
    scanIfStill();
  }

  /* ---------- Header: compact + tone ---------- */
  function setupHeader() {
    const header = document.querySelector('[data-bv2-header]');
    if (!header) return;
    const wrapper = document.querySelector('.page-wrapper');
    let queued = false;
    const toneAt = () => {
      const y = header.getBoundingClientRect().bottom + 2;
      const stack = document.elementsFromPoint(Math.round(window.innerWidth / 2), Math.round(y));
      for (const el of stack) {
        if (header.contains(el)) continue;
        const tagged = el.closest('[data-bv2-tone]');
        if (tagged) return tagged.getAttribute('data-bv2-tone');
        if (el.closest('.bv2')) return 'light';
        if (el.closest('main, footer, #footer-group')) return 'dark';
      }
      return 'dark';
    };
    const update = () => {
      queued = false;
      const top = Math.max(window.scrollY || 0, wrapper ? wrapper.scrollTop : 0);
      header.toggleAttribute('data-compact', top > 24);
      header.setAttribute('data-tone', toneAt());
    };
    const queue = () => { if (!queued) { queued = true; requestAnimationFrame(update); } };
    document.addEventListener('scroll', queue, { capture: true, passive: true });
    window.addEventListener('resize', queue, { passive: true });
    update();
  }

  /* ---------- Desktop dropdowns: hover, focus, click, Escape ----------
   * A click on a trigger always opens its panel and moves focus to the first row; a second
   * click on the same trigger (once it was opened by click) closes it. Hover opens after 60 ms,
   * and hover-then-click keeps it open. Pointer leaving closes after 220 ms unless focus is
   * inside. Escape closes and returns focus to the trigger. */
  function setupMenus() {
    document.querySelectorAll('[data-bv2-menu]').forEach((item) => {
      const trigger = item.querySelector('.bv2-nav__trigger');
      if (!trigger) return;
      let timer;
      let suppress = false;
      let openedBy = null; // 'hover' | 'focus' | 'click'
      const set = (open, by) => {
        clearTimeout(timer);
        if (open) { if (by === 'click' || !item.hasAttribute('data-open')) openedBy = by; }
        else openedBy = null;
        item.toggleAttribute('data-open', open);
        trigger.setAttribute('aria-expanded', String(open));
      };
      const closeOthers = () => document.querySelectorAll('[data-bv2-menu][data-open]').forEach((o) => {
        if (o !== item) { o.removeAttribute('data-open'); o.querySelector('.bv2-nav__trigger').setAttribute('aria-expanded', 'false'); }
      });
      item.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(timer);
        timer = setTimeout(() => { suppress = false; closeOthers(); set(true, 'hover'); }, 60);
      });
      item.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        suppress = false;
        clearTimeout(timer);
        // A menu opened by hover only stays if focus has moved into its panel; a trigger left
        // focused by an earlier click or Escape does not pin it open.
        const keep = openedBy === 'hover' ? item.querySelector('.bv2-panel') : item;
        timer = setTimeout(() => { if (!keep || !keep.contains(document.activeElement)) set(false); }, 220);
      });
      item.addEventListener('focusin', () => {
        if (suppress) return;
        closeOthers();
        set(true, 'focus');
      });
      // Safari and WebKit do not focus a button on press, so pressing the trigger blurs the row
      // inside the panel; that blur must not close the menu the click is about to toggle.
      let pressing = false;
      trigger.addEventListener('pointerdown', () => { pressing = true; });
      document.addEventListener('pointerup', () => { setTimeout(() => { pressing = false; }, 0); }, true);
      item.addEventListener('focusout', (e) => { if (!pressing && !item.contains(e.relatedTarget)) { suppress = false; set(false); } });
      trigger.addEventListener('click', () => {
        pressing = false;
        suppress = false;
        if (item.hasAttribute('data-open') && openedBy === 'click') { set(false); return; }
        closeOthers();
        set(true, 'click');
        const first = item.querySelector('.bv2-panel input:not([type="hidden"]), .bv2-panel a[href]');
        if (first) first.focus({ preventScroll: true });
      });
      item.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || !item.hasAttribute('data-open')) return;
        e.preventDefault();
        suppress = true;
        set(false);
        trigger.focus();
      });
    });
    document.addEventListener('click', (e) => {
      document.querySelectorAll('[data-bv2-menu][data-open]').forEach((o) => {
        if (!o.contains(e.target)) { o.removeAttribute('data-open'); o.querySelector('.bv2-nav__trigger').setAttribute('aria-expanded', 'false'); }
      });
    });
  }

  /* ---------- Phone and tablet drawer (#boho-nav-drawer) ----------
   * Owned here from a window capture listener, which runs before Horizon's document-level
   * on:click delegate, so a click is never dropped while Horizon's modules are still loading
   * and is never handled twice. Open: Horizon's showDialog() when the element is upgraded,
   * else <dialog>.showModal(), else a plain fixed panel (data-nav-open). Close button,
   * Escape and the backdrop all go through closeNav(), which returns focus to the button. */
  const NAV_ID = 'boho-nav-drawer';
  const BTN_ID = 'boho-header-menu-button';
  let navMode = null; // null | 'component' | 'native' | 'panel'
  let navClosing = false;
  let navFocusReturn = false;
  let navLocked = false;
  let navVerify;
  const navParts = () => {
    const comp = document.getElementById(NAV_ID);
    return { comp, dialog: comp ? comp.querySelector('dialog') : null, btn: document.getElementById(BTN_ID) };
  };
  const navExpanded = (open) => document.querySelectorAll('[aria-controls="' + NAV_ID + '"]').forEach((el) => el.setAttribute('aria-expanded', String(open)));
  const navLock = (on) => {
    if (on === navLocked) return;
    navLocked = on;
    root.toggleAttribute('scroll-lock', on);
  };
  const navFocusables = (dialog) => Array.from(dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), summary, [tabindex]:not([tabindex="-1"])')).filter((el) => el.getClientRects().length);

  function navReset() {
    clearTimeout(navVerify);
    const { comp, btn } = navParts();
    if (comp) comp.removeAttribute('data-nav-open');
    if (navMode !== 'component') navLock(false);
    navMode = null;
    navClosing = false;
    navExpanded(false);
    if (navFocusReturn && btn && btn.getClientRects().length) {
      btn.focus({ preventScroll: true });
      // WebKit can restore focus to <body> (the element it saw focused before a pressed button) after this.
      setTimeout(() => { const a = document.activeElement; if (!a || a === document.body) btn.focus({ preventScroll: true }); }, 0);
    }
    navFocusReturn = false;
  }
  function navOpenPanel() {
    const { comp, dialog } = navParts();
    navMode = 'panel';
    comp.setAttribute('data-nav-open', '');
    navLock(true);
    const close = comp.querySelector('.bv2-drawer__close');
    if (close) close.focus({ preventScroll: true });
    else if (dialog) { const f = navFocusables(dialog)[0]; if (f) f.focus({ preventScroll: true }); }
  }
  function navOpenNative() {
    const { dialog } = navParts();
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    try { dialog.showModal(); } catch (e) { return false; }
    if (!dialog.open) return false;
    navMode = 'native';
    navLock(true);
    return true;
  }
  function openNav() {
    const { comp, dialog } = navParts();
    if (!comp || navMode) return;
    navClosing = false;
    navExpanded(true);
    if (typeof comp.showDialog !== 'function' && window.customElements) { try { customElements.upgrade(comp); } catch (e) { /* not defined yet */ } }
    if (dialog && typeof comp.showDialog === 'function') {
      try {
        comp.showDialog();
        navMode = 'component';
        // Horizon opens on the next frame; if it has not by then, open it directly.
        clearTimeout(navVerify);
        navVerify = setTimeout(() => {
          if (navMode !== 'component' || dialog.open || document.visibilityState !== 'visible') return;
          navMode = null;
          if (!navOpenNative()) navOpenPanel();
        }, 300);
        return;
      } catch (e) { navMode = null; }
    }
    if (!navOpenNative()) navOpenPanel();
  }
  function closeNav(returnFocus) {
    const { comp, dialog } = navParts();
    if (!comp || !navMode || navClosing) return;
    navFocusReturn = !!returnFocus;
    navExpanded(false);
    if (navMode === 'panel' || !dialog || !dialog.open) { navReset(); return; }
    navClosing = true;
    if (navMode === 'component' && typeof comp.closeDialog === 'function') comp.closeDialog();
    else { dialog.close(); navReset(); } // the 'close' event is queued; reset now, it is idempotent
  }
  const navIsOpen = () => {
    const { comp, dialog } = navParts();
    return !!navMode || !!(comp && (comp.hasAttribute('data-nav-open') || (dialog && dialog.open)));
  };
  function onNavClose(e) {
    const { dialog } = navParts();
    if (dialog && e.target === dialog) navReset();
  }

  window.addEventListener('click', (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    if (t.closest('#' + BTN_ID)) {
      e.preventDefault();
      e.stopPropagation();
      if (navIsOpen()) {
        if (!navMode) navMode = 'component'; // opened by Horizon before this file ran
        closeNav(true);
      } else openNav();
      return;
    }
    if (!navMode) return;
    const { comp, dialog } = navParts();
    if (!comp || !dialog) return;
    if (t.closest('.bv2-drawer__close') && comp.contains(t)) {
      e.preventDefault();
      e.stopPropagation();
      closeNav(true);
      return;
    }
    let outside = false;
    if (navMode === 'panel') outside = !dialog.contains(t);
    else if (t === dialog) {
      const r = dialog.getBoundingClientRect();
      outside = e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
    }
    if (outside) { e.stopPropagation(); closeNav(true); }
  }, true);

  window.addEventListener('keydown', (e) => {
    if (!navMode) return;
    const { dialog } = navParts();
    if (!dialog) return;
    const other = Array.from(document.querySelectorAll('dialog[open]')).some((d) => d !== dialog);
    if (other) return; // a booking dialog on top owns its own keys
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      closeNav(true);
      return;
    }
    if (e.key === 'Tab' && navMode === 'panel') {
      const items = navFocusables(dialog);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (!dialog.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }, true);

  // 'cancel' and 'close' do not bubble, but capture listeners on window still hear them.
  window.addEventListener('cancel', (e) => {
    const { dialog } = navParts();
    if (!dialog || e.target !== dialog || !navMode) return;
    e.preventDefault();
    closeNav(true);
  }, true);
  window.addEventListener('close', onNavClose, true);
  window.addEventListener('resize', () => {
    if (navMode && navMode !== 'component' && window.innerWidth > 1023) closeNav(false);
  }, { passive: true });

  window.bohoV2Nav = { open: openNav, close: () => closeNav(true), mode: () => navMode };
  if (window.bohoV2NavPending) {
    window.bohoV2NavPending = false;
    if (document.getElementById(NAV_ID)) openNav();
    else document.addEventListener('DOMContentLoaded', openNav, { once: true });
  }

  /* ---------- Shopify preview bar: lift the phone bottom bar above it ----------
   * Only the theme preview injects #PBarNextFrameWrapper (fixed, 68px, bottom). Outside the
   * preview the variable is never set and the bar sits at bottom: 0. */
  function setupPreviewBar() {
    let watched = null;
    let ro = null;
    let mo = null;
    let queued = false;
    const measure = () => {
      queued = false;
      const frame = document.getElementById('PBarNextFrame');
      const el = document.getElementById('PBarNextFrameWrapper') || (frame && frame.parentElement !== document.body ? frame.parentElement : frame);
      let lift = 0;
      if (el && el.isConnected) {
        const cs = getComputedStyle(el);
        const r = el.getBoundingClientRect();
        const vh = window.innerHeight;
        if (cs.display !== 'none' && cs.visibility !== 'hidden' && cs.position === 'fixed' && r.height > 0 && r.bottom >= vh - 2 && r.top < vh) {
          lift = Math.round(Math.min(r.height, vh - r.top));
        }
      }
      if (lift > 0) root.style.setProperty('--bv2-pbar', lift + 'px');
      else root.style.removeProperty('--bv2-pbar');
      if (el !== watched) {
        if (ro) ro.disconnect();
        if (mo) mo.disconnect();
        watched = el;
        if (el) {
          if ('ResizeObserver' in window) { ro = new ResizeObserver(queue); ro.observe(el); }
          mo = new MutationObserver(queue);
          mo.observe(el, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
        }
      }
    };
    const queue = () => { if (!queued) { queued = true; requestAnimationFrame(measure); } };
    new MutationObserver(queue).observe(document.body, { childList: true });
    window.addEventListener('resize', queue, { passive: true });
    window.addEventListener('load', queue, { once: true });
    measure();
  }

  const start = () => { renderToggles(); setupHeader(); setupMenus(); setupPreviewBar(); setupLoops(); setupTap(); setupReveals(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
